import {
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from "@angular/core";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";

import { Platform, ToastController } from "@ionic/angular";
import { ApiService } from "src/app/services/api.service";
import { environment } from "src/environments/environment";
import { Subscription } from "rxjs";
import { ReCaptchaV3Service } from "ng-recaptcha";
import { isPlatformBrowser } from "@angular/common";
import { UserInterface } from "src/app/types/user";
import { EMAIL_PATTERN } from "src/app/types/form";

@Component({
  selector: "app-forgot-password",
  templateUrl: "./forgot-password.page.html",
  styleUrls: ["./forgot-password.page.scss"],
})
export class ForgotPasswordPage implements OnInit, OnDestroy {
  isBrowser: boolean = false;
  cordova: boolean = false;

  private singleExecutionSubscription: Subscription;

  /**
   * Loading state variable to toggle UI updates while request loads
   *
   * @type {boolean}
   * @memberof ForgotPasswordPage
   */
  loading: boolean = false;

  /**
   * Submitted state variable to toggle
   *
   * @type {boolean}
   * @memberof ForgotPasswordPage
   */
  submitted: boolean = false;

  /**
   * Error received from API on submit
   *
   * @type {boolean}
   * @memberof ForgotPasswordPage
   */
  errorResponse: string;

  /**
   * Success message after API submit
   *
   * @type {boolean}
   * @memberof ForgotPasswordPage
   */
  successResponse: string;

  /**
   * Reset Password form data model
   *
   * @type {boolean}
   * @memberof ForgotPasswordPage
   */
  passwordForm: FormGroup;

  @ViewChild("formTop") formTop: ElementRef;

  /**
   * Creates an instance of PasswordPage
   * @param {ApiService} API The ApiService is included to allow network requests from this component
   * @param {Router} router The module that provides navigation and URL manipulation capabilities
   * @memberof ForgotPasswordPage
   */
  constructor(
    private platform: Platform,
    private API: ApiService,
    private formBuilder: FormBuilder,
    private recaptchaV3Service: ReCaptchaV3Service,
    public toastController: ToastController,
    @Inject(PLATFORM_ID) platformId: string
  ) {
    this.isBrowser = isPlatformBrowser(platformId);

    this.platform.ready().then(() => {
      this.cordova = this.platform.is("cordova");
    });
  }

  createForm() {
    this.passwordForm = this.formBuilder.group({
      email: ["", [Validators.required, Validators.pattern(EMAIL_PATTERN)]],
    });
  }

  executeAction() {
    return new Promise((resolve, reject) => {
      if (this.singleExecutionSubscription) {
        this.singleExecutionSubscription.unsubscribe();
      }
      this.singleExecutionSubscription = this.recaptchaV3Service
        .execute("importantAction")
        .subscribe(
          (token) => {
            resolve(token);
          },
          (error) => {
            reject(error);
          }
        );
    });
  }

  onSubmit(formData) {
    this.loading = true;
    this.submitted = true;
    this.successResponse = null;

    if (this.passwordForm.valid) {
      this.errorResponse = null;

      this.executeAction().then(
        (token) => {
          const formDataValues: UserInterface = {
            ...formData,
            reCaptcha: token,
            source: environment.mall_id,
            platform: this.platform.is("android")
              ? "android"
              : this.platform.is("ios")
              ? "ios"
              : this.platform.is("mobile") || this.platform.is("mobileweb")
              ? "mobile"
              : "desktop",
          };

          this.API.POST("forgot-password", formDataValues, true, {
            "mall-referer":
              typeof window !== "undefined"
                ? window.location.origin
                : environment.environment_name,
          }).subscribe({
            next: (response) => {
              this.loading = false;
              this.submitted = false;

              this.successResponse =
                "Password reset instructions have been emailed to " +
                formDataValues.email;
              this.passwordForm.reset();

              // TODO - Log anonymous user activity - API support required
              // this.API.PUT("shoppers/tracking/log", {
              //   action: "forgotPassword",
              //   title: formData.email,
              //   url: typeof window !== "undefined"
              //     ? window.location.href
              //     : this.platform.is("android")
              //     ? "android"
              //     : this.platform.is("ios"),
              //   mallId: environment.mall_id,
              // }).subscribe();
            },
            error: (response) => {
              this.loading = false;

              if (
                response.error?.message &&
                typeof response.error.message === "string"
              ) {
                this.errorResponse = response.error.message;
              } else if (response && typeof response === "string") {
                this.errorResponse = response;
              } else {
                this.errorResponse = "An error occured. Please try again";
              }

              this.formTop.nativeElement.scrollIntoView({
                behavior: "smooth",
                block: "end",
              });
            },
          });
        },
        (error) => {
          this.loading = false;
          this.errorResponse = error;
          this.formTop.nativeElement.scrollIntoView({
            behavior: "smooth",
            block: "end",
          });
        }
      );
    } else {
      this.loading = false;
      this.errorResponse =
        "Please ensure all required fields have been completed";
      this.formTop.nativeElement.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }

  ngOnInit() {
    this.createForm();
  }

  ngOnDestroy() {
    if (this.isBrowser && !this.cordova && this.singleExecutionSubscription) {
      this.singleExecutionSubscription.unsubscribe();
    }
  }
}
